import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { supabase } from "../services/supabaseClient";

const EXEMPT_PATHS = [
  "/admin", "/login", "/status", "/forgot-password",
  "/reset-password", "/auth/callback", "/verify-email",
];

export default function GlobalEmergencyGate() {
  const location = useLocation();
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("uniair_public_emergency_state_v1");
      if (error) throw error;
      if (data?.ok && typeof data.mode === "string") setState(data);
    } catch {
      // The database gate remains authoritative even if the UI cannot refresh.
      // Retain the last known emergency state instead of clearing the notice.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const check = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const interval = window.setInterval(check, 15_000);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [refresh]);

  const exempt = EXEMPT_PATHS.some((path) =>
    location.pathname === path || location.pathname.startsWith(path + "/")
  );
  if (state?.mode !== "lockdown" || exempt) return null;

  return (
    <div className="fixed inset-0 z-[200] flex min-h-screen items-center justify-center overflow-y-auto bg-[#f9f7f6] p-5" role="alertdialog" aria-modal="true" aria-labelledby="uniair-emergency-heading">
      <main className="w-full max-w-lg rounded-3xl border border-[#720e1e]/15 bg-white p-7 text-center shadow-lg sm:p-10">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#720e1e]/10 text-[#720e1e]">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <p className="mt-6 text-sm font-extrabold tracking-wide text-[#720e1e]">UniAir</p>
        <h1 id="uniair-emergency-heading" className="mt-3 text-2xl font-bold tracking-tight text-slate-950">Temporarily unavailable</h1>
        <p className="mt-4 text-sm leading-7 text-slate-600">
          {state.public_message || "UniAir is temporarily unavailable. Please check back later."}
        </p>
        <p className="mt-5 text-xs leading-6 text-slate-500">
          For an ongoing emergency, contact local emergency services immediately.
          Existing bookings and payment records have not been deleted.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/status" className="rounded-xl bg-[#720e1e] px-5 py-3 text-sm font-bold text-white">Service status</Link>
          <Link to="/login" className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">Staff sign in</Link>
        </div>
      </main>
    </div>
  );
}
